/**
 * useBrands.jsx
 * Brand list + "which brand am I working as" selection, shared by every screen.
 * The selected brand is remembered between sessions.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api.js';

const BrandContext = createContext(null);
const STORAGE_KEY = 'asm.selectedBrandId';

export function BrandProvider({ children }) {
  const [brands, setBrands] = useState([]);
  const [selectedId, setSelectedId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { brands: list } = await api.brands.list();
      setBrands(list);
      setError(null);
      // Selection can go stale if a brand was archived elsewhere.
      setSelectedId((current) => {
        if (current && list.some((b) => b.id === current)) return current;
        return list[0]?.id ?? null;
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (selectedId) localStorage.setItem(STORAGE_KEY, selectedId);
  }, [selectedId]);

  const value = useMemo(
    () => ({
      brands,
      loading,
      error,
      refresh,
      selectedId,
      selectBrand: setSelectedId,
      selectedBrand: brands.find((b) => b.id === selectedId) ?? null,
    }),
    [brands, loading, error, refresh, selectedId],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrands() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrands must be used inside <BrandProvider>');
  return ctx;
}

/**
 * The brand this session is working as, shaped like useState so a screen can
 * drop it straight into a <select>.
 *
 * Every screen used to keep its own copy of this, defaulting to the first
 * brand in the list. That is how a batch of PeekoType artwork ended up in
 * Archevot's library: the picture was converted on one screen, saved, and
 * looked for on another that had quietly reset itself to the first brand.
 * Choosing a brand anywhere now means choosing it everywhere, and the choice
 * survives a reload.
 */
export function useSelectedBrand() {
  const { selectedId, selectBrand, brands } = useBrands();
  // Never hand back an id that is no longer in the list — a <select> with a
  // value matching none of its options renders blank and reads as broken.
  const valid = selectedId && brands.some((b) => b.id === selectedId);
  return [valid ? selectedId : (brands[0]?.id ?? ''), selectBrand];
}
